import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Award, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function MyCertificates({ user }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState({ open: false, cert: null, text: '', url: '', hashtags: '' });
  const navigate = useNavigate();

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const response = await fetch(`${API}/certificates/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCertificates(data);
      }
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certificateId, studentName, moduleName) => {
    try {
      const response = await fetch(`${API}/certificates/${certificateId}/download`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate_${studentName.replace(/\s+/g, '_')}_${moduleName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Certificate downloaded!');
      } else {
        toast.error('Failed to download certificate');
      }
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  const handleShare = (cert) => {
    const text = `🎓 I just completed the "${cert.module_name}" module at J423 Community Discipleship Center! Excited to continue my spiritual journey. #Faith #BibleStudy #ChristianEducation`;
    const hashtags = 'J423Community,BibleStudy,SpiritualGrowth';
    const url = window.location.origin;
    
    // Create share options
    const shareData = {
      title: `Certificate: ${cert.module_name}`,
      text: text,
      url: url
    };
    
    // Check if Web Share API is available (mobile)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData).catch(() => {});
    } else {
      // Desktop: show share options modal
      setShareModal({ open: true, cert, text, url, hashtags });
    }
  };

  const shareToTwitter = (text, url, hashtags) => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=${hashtags}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = (url) => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  const shareToLinkedIn = (url, text) => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
  };

  const copyShareLink = (text, url) => {
    navigator.clipboard.writeText(`${text}\n\n${url}`);
    toast.success('Copied to clipboard! Share it anywhere.');
    setShareModal({ open: false });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading certificates...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-10 h-10 text-[#b4844a]" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              My Certificates
            </h1>
          </div>
          <p className="text-lg text-[#588157]">Your achievements and completed modules</p>
        </div>

        {certificates.length === 0 ? (
          <Card className="p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <Award className="w-20 h-20 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-[#344E41] mb-2">No Certificates Yet</h3>
            <p className="text-[#588157] mb-6">
              Complete all courses in a module to earn your certificate
            </p>
            <Button
              onClick={() => navigate('/courses')}
              className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 rounded-lg"
            >
              Browse Courses
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <Card
                key={cert.certificate_id}
                className="p-6 bg-[#FEFAE0] border-2 border-[#b4844a]/30 shadow-lg hover:shadow-xl duration-300 hover:-translate-y-1 rounded-2xl"
                data-testid={`certificate-${cert.certificate_id}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#b4844a] to-[#d4a373] rounded-full flex items-center justify-center mb-4 shadow-lg">
                    <Award className="w-10 h-10 text-white" strokeWidth={2} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                    {cert.module_name}
                  </h3>
                  
                  <p className="text-sm text-[#588157] mb-1">
                    Awarded to <strong>{cert.student_name}</strong>
                  </p>
                  
                  <p className="text-xs text-[#A3B18A] mb-4">
                    {formatDistanceToNow(new Date(cert.issued_at), { addSuffix: true })}
                  </p>
                  
                  <div className="w-full space-y-2">
                    <Button
                      onClick={() => handleDownload(cert.certificate_id, cert.student_name, cert.module_name)}
                      data-testid={`download-${cert.certificate_id}`}
                      className="w-full bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 active:scale-95 font-medium rounded-lg"
                    >
                      <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Download PDF
                    </Button>
                    
                    <Button
                      onClick={() => handleShare(cert)}
                      variant="outline"
                      data-testid={`share-${cert.certificate_id}`}
                      className="w-full border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg"
                    >
                      <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Share
                    </Button>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#CCD5AE] w-full">
                    <p className="text-xs text-[#A3B18A]">
                      Certificate ID: {cert.certificate_id}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Share Modal */}
      {shareModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShareModal({ open: false })}>
          <Card className="max-w-md w-full bg-[#FEFAE0] border-[#CCD5AE] rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                Share Your Achievement
              </h3>
              <button
                onClick={() => setShareModal({ open: false })}
                className="text-[#588157] hover:text-[#344E41]"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 bg-[#E9EDC9] rounded-lg">
              <p className="text-sm text-[#344E41]">{shareModal.text}</p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => shareToTwitter(shareModal.text, shareModal.url, shareModal.hashtags)}
                className="w-full bg-[#1DA1F2] text-white hover:bg-[#1a8cd8] shadow-md duration-300 rounded-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
                Share on Twitter
              </Button>

              <Button
                onClick={() => shareToFacebook(shareModal.url)}
                className="w-full bg-[#4267B2] text-white hover:bg-[#365899] shadow-md duration-300 rounded-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Share on Facebook
              </Button>

              <Button
                onClick={() => shareToLinkedIn(shareModal.url, shareModal.text)}
                className="w-full bg-[#0077B5] text-white hover:bg-[#006399] shadow-md duration-300 rounded-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Share on LinkedIn
              </Button>

              <Button
                onClick={() => copyShareLink(shareModal.text, shareModal.url)}
                variant="outline"
                className="w-full border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Link
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
